import { IOrganizations } from "../types/Organisation";

/** Display status of an organization row in the Organizations page / request modal.
 * Adds AWAITING_DOCUMENTS on top of the backend's verificationStatus: a PENDING
 * organization that hasn't completed its activation (documents, bank account and
 * national address — see isActivationComplete) isn't reviewable, so it reads as
 * "بانتظار استكمال البيانات" instead of "قيد المراجعة". The key keeps its original
 * name because the chip styles are derived from it. */
export type TOrganizationRequestStatus =
  | "AWAITING_DOCUMENTS"
  | "PENDING"
  | "REJECTED"
  | "VERIFIED";

export const ORGANIZATION_REQUEST_STATUS_LABELS: Record<
  TOrganizationRequestStatus,
  string
> = {
  AWAITING_DOCUMENTS: "بانتظار استكمال البيانات",
  PENDING: "قيد المراجعة",
  REJECTED: "مرفوض",
  VERIFIED: "مفعل",
};

/**
 * Whether the organization completed the portal's activation step: documents, a bank
 * account and a national address — the same three the portal requires before review.
 *
 * @param hasDocuments whether the organization uploaded at least one supporting
 * document. `undefined` means "not known yet" (documents still loading) and is
 * treated as missing, so actions stay disabled until we know for certain.
 */
export const isActivationComplete = (
  organization?: IOrganizations,
  hasDocuments?: boolean
): boolean =>
  Boolean(hasDocuments) &&
  Boolean(organization?.bankAccount?.iban) &&
  Boolean(organization?.address?.nationalAddress);

export const getOrganizationRequestStatus = (
  organization?: IOrganizations,
  hasDocuments?: boolean
): TOrganizationRequestStatus => {
  const status = organization?.verificationStatus || "VERIFIED";
  if (status === "PENDING" && !isActivationComplete(organization, hasDocuments))
    return "AWAITING_DOCUMENTS";
  return status;
};

/** An admin can only accept/reject a request once the organization has completed its
 * activation — there is nothing to review otherwise. */
export const canReviewOrganizationRequest = (
  organization?: IOrganizations,
  hasDocuments?: boolean
): boolean =>
  organization?.verificationStatus === "PENDING" &&
  isActivationComplete(organization, hasDocuments);
